// Bridges the "kanalearn:consent" DOM event dispatched from public/index.html
// (wired to iubenda's onConsentGiven/onPreferenceExpressed callbacks) into a
// simple granted/not-granted boolean for the rest of the analytics module.
// Deliberately knows nothing about iubenda's specific API shape beyond the
// event name - see the comment in public/index.html for why.

import type { ConsentListener } from "./types";

const CONSENT_EVENT = "kanalearn:consent";

let granted = false;
const listeners = new Set<ConsentListener>();

// iubenda's callback payload shape varies by exact preference-center
// configuration; read defensively and only report "granted" on an
// unambiguous positive signal - anything else stays not-granted, which is
// the default. iubenda measurement consent is purpose 4.
function extractGranted(preference: unknown): boolean {
    if (!preference || typeof preference !== "object") return false;
    const candidate = preference as { consent?: unknown; purposes?: unknown };
    if (candidate.consent === false) return false;
    if (candidate.purposes && typeof candidate.purposes === "object") {
        return (candidate.purposes as Record<string, unknown>)["4"] === true;
    }
    return candidate.consent === true;
}

function handleConsentEvent(event: Event): void {
    const next = extractGranted((event as CustomEvent<unknown>).detail);
    if (next === granted) return;
    granted = next;
    listeners.forEach((listener) => listener(granted));
}

if (typeof window !== "undefined") {
    window.addEventListener(CONSENT_EVENT, handleConsentEvent);
    // A returning visitor's preference can arrive before the bundle loads.
    const consentWindow = window as Window & { _iub?: { cs?: { consent?: unknown } } };
    granted = extractGranted(consentWindow._iub?.cs?.consent);
}

export function hasAnalyticsConsent(): boolean {
    return granted;
}

// Returns an unsubscribe function. Does NOT call the listener immediately
// with the current state - callers that need the current value on
// subscription should also call hasAnalyticsConsent() themselves.
export function onConsentChange(listener: ConsentListener): () => boolean {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

// Test-only escape hatch: production code should never call this directly,
// consent state is only ever driven by the real browser event above.
export function __setConsentForTests(value: boolean): void {
    granted = value;
    listeners.forEach((listener) => listener(granted));
}
